/**
 * Shared signed-in favorite toggle for AccountShopContactsMachine (hub unfavorite + tests).
 */
import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { invalidatePublicCustomerSalesPointsCache } from '../../shared/api/customerDirectoryCacheInvalidate.js';
import {
  PLATFORM_DIRECTORY_SHOPS_TENANT,
  readDirectoryShopsPersist,
} from '../../shared/session/searchPersistStorage.js';
import { showToast } from '../../shared/ui/toastStore.js';
import { setSalesPointFavorite } from './salesPointContactsApi.js';
import type { SalesPointContactItemDTO } from './salesPointContactsTypes.js';
import { syncGuestFavoriteOptOutFromAccountToggle } from './pendingFavoriteSpContacts.js';

function isUsableTenantCode(code: unknown): code is string {
  return typeof code === 'string' && code.trim().length > 0;
}

export type ApplyAccountShopFavoriteToggleArgs = {
  readonly apiBaseUrl: string;
  readonly item: SalesPointContactItemDTO;
  readonly favorite: boolean;
  readonly session: { readonly customerId: number } | null | undefined;
  readonly queryClient: QueryClient;
  readonly t: TFunction<'customer'>;
  readonly onPendingId: (salesPointId: number | null) => void;
  readonly onClearUnfavoriteTarget: () => void;
};

export async function applyAccountShopFavoriteToggle(
  args: ApplyAccountShopFavoriteToggleArgs,
): Promise<void> {
  const {
    apiBaseUrl,
    item,
    favorite,
    session,
    queryClient,
    t,
    onPendingId,
    onClearUnfavoriteTarget,
  } = args;
  onPendingId(item.salesPointId);
  try {
    await setSalesPointFavorite({
      apiBaseUrl,
      body: { salesPointId: item.salesPointId, favorite },
    });
    syncGuestFavoriteOptOutFromAccountToggle(
      {
        salesPointId: item.salesPointId,
        tenantCode: item.tenantCode,
        salesPointSlug: item.code,
      },
      favorite,
    );
    const customerKey = session?.customerId ?? 'bearer';
    await queryClient.invalidateQueries({
      queryKey: customerQueryKeys.salesPointContacts(customerKey),
    });
    const platformSort = readDirectoryShopsPersist(PLATFORM_DIRECTORY_SHOPS_TENANT)?.sort;
    const tenantSort = isUsableTenantCode(item.tenantCode)
      ? readDirectoryShopsPersist(item.tenantCode)?.sort
      : undefined;
    if (platformSort === 'favoriteDesc' || tenantSort === 'favoriteDesc') {
      invalidatePublicCustomerSalesPointsCache(queryClient, apiBaseUrl);
    }
    onClearUnfavoriteTarget();
  } catch {
    showToast({
      message: t('errors.spContact.recordTransport'),
      variant: 'danger',
    });
  } finally {
    onPendingId(null);
  }
}
